<template>
  <div
    class="summary-details"
    :class="{
      scrolled: detailsScrolled,
      'scrolled-end': detailsScrolledEnd,
    }"
  >
    <div class="summary-details-horizontal-scroller">
      <span
        v-observe-visibility="{
          callback: summaryBarFirstItemCallback,
          throttle: 200,
          throttleOptions: { leading: 'both' },
        }"
      />
      <span class="summary-bar-group dates-and-party">
        <search-summary-bar-details-calendar
          v-if="hasDates"
          :date-range="searchFilters.dates"
          :slug="slug"
        />
        <search-summary-bar-link-matches
          v-else
          :slug="slug"
          class="summary-bar-action"
          exclude-type="visibleMarker"
        >
          <translate tag="p">Add dates</translate>
        </search-summary-bar-link-matches>
        <search-summary-bar-details-guests
          :search-filters="searchFilters"
          :slug="slug"
        />
      </span>
      <search-summary-bar-details-categories
        :search-filters="searchFilters"
        :slug="slug"
      />
      <search-summary-bar-details-facets
        v-if="!slug"
        class="summary-bar-group"
        :search-filters="searchFilters"
      />
      <span class="summary-bar-group">
        <search-summary-bar-details-bounds
          v-if="isBoundsSearch"
          :search-filters="searchFilters"
        />
        <search-summary-bar-details-price-range
          :search-filters="searchFilters"
        />
      </span>
      <span
        v-observe-visibility="{
          callback: summaryBarLastItemCallback,
          throttle: 300,
          throttleOptions: { leading: 'both' },
        }"
        class="end-stop"
      />
    </div>
  </div>
</template>

<script lang="ts">
import type { SearchFilters } from '../../../apps/search/schemas'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

@Component
class SearchSummaryBarDetails extends Vue {
  @Prop()
  searchFilters: SearchFilters

  @Prop({ required: false })
  slug?: string

  detailsScrolled = false
  detailsScrolledEnd = false

  get hasDates() {
    const dates = this.searchFilters.dates
    if (!dates || (!dates.arrive && !dates.depart)) {
      return false
    }
    return true
  }

  get isBoundsSearch(): boolean {
    return !!this.searchFilters.bounds
  }

  summaryBarFirstItemCallback(isVisible: boolean) {
    this.detailsScrolled = !isVisible
  }

  summaryBarLastItemCallback(isVisible: boolean) {
    this.detailsScrolledEnd = !isVisible
  }
}

export default toNative(SearchSummaryBarDetails)
</script>
<style scoped>
.summary-details {
  height: 2.8em;
  overflow: hidden;
  padding: 0.5em 0;
  position: relative;
}

.summary-details-horizontal-scroller {
  align-items: stretch;
  align-self: center;
  display: flex;
  flex-direction: row;
  height: 100%;
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: visible;
  padding: 0 1em 0 0.65em;
  scroll-behavior: smooth;
  scrollbar-width: none;
  transition: border ease-in-out 0.15s;
  width: 100%;
}

.summary-details.show-summary-bar-action .summary-details-horizontal-scroller {
  border-left: 1px solid #ccc;
}

.summary-details:before {
  background: radial-gradient(
    at -30% center,
    rgba(0, 0, 0, 0.75) 0%,
    rgba(0, 0, 0, 0) 70%
  );
  content: '';
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 0.35em;
  z-index: 1;
  transition: opacity 0.2s ease-in-out;
  opacity: 0;
}

.summary-details:after {
  background: linear-gradient(
    to left,
    rgba(255, 255, 255, 1) 10%,
    rgba(255, 255, 255, 0.9) 20%,
    rgba(255, 255, 255, 0) 100%
  );
  content: '';
  height: 100%;
  right: 0;
  left: auto;
  top: 0;
  bottom: 0;
  position: absolute;
  width: 3em;
  z-index: 1;
  transition: opacity 0.35s ease-in-out;
  opacity: 0;
  display: block;
  pointer-events: none;
}

.summary-details.scrolled:before,
.summary-details.scrolled-end:after {
  opacity: 1;
}

.summary-details.scrolled .summary-details-horizontal-scroller {
  border-left-color: transparent;
}

.summary-details-horizontal-scroller::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.end-stop {
  display: block;
  flex-shrink: 0;
  height: auto;
  width: 2em;
}

.summary-bar-group,
:deep(.summary-bar-group) {
  align-items: stretch;
  align-self: center;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  height: 100%;
  justify-content: flex-start;
}

.summary-bar-group.dates-and-party :deep(a) {
  background: var(--colour-mid-green);
}

.summary-bar-group :deep(a),
:deep(.summary-bar-group a) {
  align-items: center;
  background: var(--colour-mid-blue);
  border-radius: 0.35em;
  color: white;
  display: flex;
  flex-shrink: 0;
  font-size: var(--body);
  margin-right: 0.6em;
  padding: 0.25em 0.35em;
  text-decoration: none;
  white-space: nowrap;
}

.summary-bar-group :deep(a:hover),
:deep(.summary-bar-group a:hover),
.summary-bar-group :deep(a:active),
:deep(.summary-bar-group a:active) {
  text-decoration: none;
}

.summary-bar-group :deep(a .icon),
:deep(.summary-bar-group a .icon) {
  margin: 0 0 0 0.3em;
  opacity: 0.7;
}

.summary-bar-group :deep(a .display-price) {
  font-weight: normal;
}

.summary-bar-group :deep(img),
:deep(.summary-bar-group img) {
  display: inline-block;
  filter: brightness(0) grayscale(1) invert(1);
  height: 1.2em;
  width: auto;
}

.summary-bar-group :deep(.category-icons),
:deep(.summary-bar-group .category-icons) {
  display: flex;
}

.summary-bar-group :deep(.category-icons img),
:deep(.summary-bar-group .category-icons img) {
  filter: brightness(0) grayscale(1) invert(1);
  height: 1.2em;
  width: auto;
}
</style>
